/**
 * MemberProfileCard — "Full Dossier v2" profile card for the Feed area.
 *
 * Design D v2: Gamified profile card with level & respect rank systems,
 * animated progress bars, rank ladders, detail chips, bio, and action buttons.
 *
 * Uses existing member data fields (accountLevel, respectCount, membershipTier,
 * userBio, userAvatar, isOnline, createdAt). No backend changes needed.
 */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Flex,
  Badge,
  Button,
  IconButton,
  Tooltip,
  Collapse,
  useToast
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { ChatIcon, CloseIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { FaPlus, FaHandshake, FaCoins, FaExclamationTriangle, FaCheck, FaTv, FaStar } from 'react-icons/fa';
import BurrowAvatar from '../UI/BurrowAvatar';
import useRiseShatter from '../../hooks/useRiseShatter';
import apiClient from '../../utils/apiConfig';
import { LEVEL_RANKS, RESPECT_RANKS, memberLevelRank } from '../../utils/burrowRanks';


// ─── Orbiting Respect Stars (profile-sized, semi-circle around avatar bottom) ──
const PROFILE_ORBIT = {
  1: [{ left: 30, top: 72 }],
  2: [{ left: 24, top: 70 }, { left: 36, top: 70 }],
  3: [{ left: 30, top: 60 }, { left: 24, top: 70 }, { left: 36, top: 70 }],
  4: [{ left: 24, top: 60 }, { left: 36, top: 60 }, { left: 24, top: 70 }, { left: 36, top: 70 }],
  5: [{ left: 30, top: 50 }, { left: 24, top: 60 }, { left: 36, top: 60 }, { left: 24, top: 70 }, { left: 36, top: 70 }],
};

const ProfileOrbitStars = ({ count = 0 }) => {
  const filled = Math.min(Math.max(count, 0), 5);
  if (filled === 0) return null;
  const positions = PROFILE_ORBIT[filled];
  return (
    <>
      {positions.map((pos, i) => (
        <Box
          key={i}
          as={FaStar}
          position="absolute"
          left={`${pos.left}px`}
          top={`${pos.top}px`}
          color="#F6E05E"
          fontSize="10px"
          filter="drop-shadow(0 0 3px rgba(246,224,94,0.6))"
          zIndex={3}
        />
      ))}
    </>
  );
};

/**
 * Given a value and a ranks array, returns:
 *   { rank, rankIndex, nextRank, progress }
 */
function computeRank(value, ranks) {
  let rankIndex = 0;
  for (let i = ranks.length - 1; i >= 0; i--) {
    if (value >= ranks[i].threshold) {
      rankIndex = i;
      break;
    }
  }

  const rank = ranks[rankIndex];
  const nextRank = rankIndex < ranks.length - 1 ? ranks[rankIndex + 1] : null;

  let progress = 1; // default: maxed out
  if (nextRank) {
    const rangeStart = rank.threshold;
    const rangeEnd = nextRank.threshold;
    progress = Math.min((value - rangeStart) / (rangeEnd - rangeStart), 1);
  }

  return { rank, rankIndex, nextRank, progress };
}

// ─── Keyframe Animations ────────────────────────────────────────────

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 3px rgba(72,187,120,0.5); }
  50% { box-shadow: 0 0 10px rgba(72,187,120,0.8); }
`;


// ─── Sub-components ─────────────────────────────────────────────────

/** Animated progress bar with shimmer overlay */
const RankProgressBar = ({ progress, colorStart, colorEnd }) => (
  <Box
    w="100%"
    h="8px"
    bg="rgba(255,255,255,0.06)"
    borderRadius="4px"
    overflow="hidden"
    mb="6px"
    position="relative"
  >
    <Box
      h="100%"
      borderRadius="4px"
      position="relative"
      transition="width 1s ease"
      w={`${Math.round(progress * 100)}%`}
      bgGradient={`linear(to-r, ${colorStart}, ${colorEnd})`}
      boxShadow={`0 0 8px ${colorStart}66`}
      overflow="hidden"
    >
      {/* Shimmer overlay */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bgGradient="linear(to-r, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)"
        animation={`${shimmer} 2s infinite`}
      />
    </Box>
  </Box>
);


/** Rank ladder showing all milestones */
const RankLadder = ({ ranks, currentIndex }) => (
  <Flex
    justify="space-between"
    align="center"
    mt="8px"
    pt="8px"
    borderTop="1px solid rgba(255,255,255,0.04)"
  >
    {ranks.map((r, i) => {
      const isAchieved = i < currentIndex;
      const isCurrent = i === currentIndex;
      return (
        <VStack
          key={r.name}
          spacing="3px"
          opacity={isAchieved || isCurrent ? 1 : 0.3}
          transition="opacity 0.3s"
        >
          <Text fontSize="14px" lineHeight={1}>{r.icon}</Text>
          <Text
            fontSize="7px"
            textTransform="uppercase"
            letterSpacing="0.3px"
            whiteSpace="nowrap"
            color={isCurrent ? '#F6E05E' : isAchieved ? '#888' : '#555'}
            fontWeight={isCurrent ? 700 : 400}
          >
            {r.name}{isCurrent ? ' ★' : ''}
          </Text>
        </VStack>
      );
    })}
  </Flex>
);


/** Single rank panel (Level or Respect) — tap to expand/collapse rank ladder */
const RankPanel = ({ label, value, valueLabel, ranks, colorStart, colorEnd, stripGradient, unknown = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { rank, rankIndex, nextRank, progress } = useMemo(
    () => computeRank(value, ranks),
    [value, ranks]
  );

  const isLevel = ranks === LEVEL_RANKS;

  return (
    <Box
      bg="rgba(255,255,255,0.03)"
      borderRadius="12px"
      p="14px 16px"
      border="1px solid"
      borderColor={isExpanded ? `${colorStart}33` : 'rgba(255,255,255,0.05)'}
      mb="10px"
      position="relative"
      overflow="hidden"
      cursor="pointer"
      onClick={() => setIsExpanded(prev => !prev)}
      transition="border-color 0.25s"
      _hover={{ borderColor: `${colorStart}22` }}
    >
      {/* Rank header row */}
      <Flex align="center" justify="space-between" mb="10px">
        <HStack spacing="10px">
          {/* Rank icon box */}
          <Flex
            w="36px"
            h="36px"
            borderRadius="8px"
            align="center"
            justify="center"
            fontSize="18px"
            bg={isLevel ? 'rgba(246,224,94,0.12)' : 'rgba(72,187,120,0.12)'}
            boxShadow={isLevel ? '0 0 12px rgba(246,224,94,0.1)' : '0 0 12px rgba(72,187,120,0.1)'}
          >
            {rank.icon}
          </Flex>
          <Box>
            <Text fontSize="14px" fontWeight={700} color={isLevel ? '#F6E05E' : '#48BB78'}>
              {rank.name}
            </Text>
            <Text fontSize="10px" color="#666" mt="1px">
              Rank {rankIndex} of {ranks.length - 1}
            </Text>
          </Box>
        </HStack>
        <HStack spacing="8px">
          <Box textAlign="right">
            <Text fontSize="24px" fontWeight={800} lineHeight={1} color={isLevel ? '#F6E05E' : '#48BB78'}>
              {unknown ? '—' : value}
            </Text>
            <Text fontSize="8px" color="#555" textTransform="uppercase" letterSpacing="1px">
              {valueLabel}
            </Text>
          </Box>
          {/* Expand/collapse chevron */}
          <ChevronDownIcon
            color="#555"
            boxSize={4}
            transition="transform 0.25s ease"
            transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
          />
        </HStack>
      </Flex>

      {/* Progress bar — always visible */}
      <RankProgressBar progress={progress} colorStart={colorStart} colorEnd={colorEnd} />

      {/* Progress labels — always visible */}
      <Flex justify="space-between" fontSize="9px" color="#555">
        <Text>
          {isLevel
            ? `${rank.threshold.toLocaleString()} BP — ${rank.name}`
            : `${rank.threshold === 1 ? '1 person' : `${rank.threshold} people`} — ${rank.name}`}
        </Text>
        {nextRank ? (
          <Text color="#888">
            {isLevel
              ? `at ${nextRank.threshold.toLocaleString()} BP → ${nextRank.icon} ${nextRank.name}`
              : `${nextRank.threshold === 1 ? '1 person' : `${nextRank.threshold} people`} → ${nextRank.icon} ${nextRank.name}`}
          </Text>
        ) : (
          <Text color="#888">MAX RANK</Text>
        )}
      </Flex>

      {/* Collapsed hint when closed */}
      {!isExpanded && (
        <Text fontSize="8px" color="#444" textAlign="center" mt="6px">
          Tap to see all ranks
        </Text>
      )}

      {/* Rank ladder — collapsible */}
      <Collapse in={isExpanded} animateOpacity>
        <RankLadder ranks={ranks} currentIndex={rankIndex} />
      </Collapse>
    </Box>
  );
};


/** Collapsible bio — short bios show in full, long bios collapse with tap to expand */
const BIO_COLLAPSE_THRESHOLD = 80; // characters before we collapse

const CollapsibleBio = ({ bio }) => {
  const text = bio || 'No bio available';
  const isLong = text.length > BIO_COLLAPSE_THRESHOLD;
  const [isExpanded, setIsExpanded] = useState(!isLong);

  return (
    <Box mb="16px">
      <Flex
        align="center"
        justify="space-between"
        mb="6px"
        cursor={isLong ? 'pointer' : 'default'}
        onClick={() => isLong && setIsExpanded(prev => !prev)}
      >
        <Text fontSize="11px" color="#666" textTransform="uppercase" letterSpacing="0.5px">
          About
        </Text>
        {isLong && (
          <HStack spacing="4px">
            <Text fontSize="8px" color="#555">
              {isExpanded ? 'Collapse' : 'Read more'}
            </Text>
            <ChevronDownIcon
              color="#555"
              boxSize={3}
              transition="transform 0.25s ease"
              transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
            />
          </HStack>
        )}
      </Flex>
      <Box
        bg="rgba(0,0,0,0.2)"
        borderRadius="10px"
        border="1px solid rgba(255,255,255,0.04)"
        p="12px"
        cursor={isLong ? 'pointer' : 'default'}
        onClick={() => isLong && setIsExpanded(prev => !prev)}
      >
        <Text
          fontSize="13px"
          color={bio ? '#bbb' : '#666'}
          fontStyle={bio ? 'normal' : 'italic'}
          lineHeight={1.6}
          noOfLines={isExpanded ? undefined : 2}
          transition="all 0.25s ease"
        >
          {text}
        </Text>
      </Box>
    </Box>
  );
};


// ─── Main Component ─────────────────────────────────────────────────

const MemberProfileCard = ({ member, onClose, onAddToKin, onMessage, onGiftMU, onReport, isKin }) => {
  const lvl = memberLevelRank(member);
  const level = lvl.known ? Number(member.burrowPoints) : lvl.threshold; // real BP; legacy floor only if unknown
  const levelUnknown = !lvl.known; // payload had no real burrowPoints → show '—' not a fake total
  const respect = member?.respectCount || 0;
  const tier = member?.membershipTier || 'base';

  // Respect button state
  const [hasGivenRespect, setHasGivenRespect] = useState(false);
  const [localRespectCount, setLocalRespectCount] = useState(respect);
  const [respectLoading, setRespectLoading] = useState(false);
  const respectBtnRef = useRef(null);
  const triggerRiseShatter = useRiseShatter();
  const toast = useToast();

  // Sync local respect count when member changes
  useEffect(() => {
    setLocalRespectCount(member?.respectCount || 0);
  }, [member?.respectCount]);

  // Check if current user has already given respect to this member
  useEffect(() => {
    if (!member?.burrowerId) return;
    let cancelled = false;
    apiClient.get(`/api/user/respect-status/${member.burrowerId}`)
      .then(res => {
        if (!cancelled && res.data.success) {
          setHasGivenRespect(res.data.hasGivenRespect);
        }
      })
      .catch(() => {}); // Silently fail — button defaults to "not given"
    return () => { cancelled = true; };
  }, [member?.burrowerId]);

  // ─── Toggle Respect Handler (optimistic + rise-shatter) ───
  const handleToggleRespect = async () => {
    if (respectLoading) return;
    setRespectLoading(true);

    const wasGiven = hasGivenRespect;

    // Optimistic update
    setHasGivenRespect(!wasGiven);
    setLocalRespectCount(prev => wasGiven ? Math.max(0, prev - 1) : prev + 1);

    // Rise-shatter animation
    if (!wasGiven) {
      triggerRiseShatter(respectBtnRef.current, '🤝 Respected!', '#48BB78');
    } else {
      triggerRiseShatter(respectBtnRef.current, '✕ Revoked', '#F56565');
    }

    try {
      const res = await apiClient.post(`/api/user/give-respect/${member.burrowerId}`);
      if (res.data.success) {
        setLocalRespectCount(res.data.newRespectCount);
        setHasGivenRespect(res.data.action === 'added');
      }
    } catch (err) {
      // Rollback optimistic update
      setHasGivenRespect(wasGiven);
      setLocalRespectCount(prev => wasGiven ? prev + 1 : Math.max(0, prev - 1));

      const msg = err.response?.data?.message || 'Failed to toggle respect';
      toast({
        title: msg,
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setRespectLoading(false);
    }
  };

  // Format join date
  const joinDate = useMemo(() => {
    if (!member?.createdAt) return null;
    try {
      const d = new Date(member.createdAt);
      return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch {
      return null;
    }
  }, [member?.createdAt]);

  // Tier badge color
  const tierColor = useMemo(() => {
    switch (tier) {
      case 'vip': return { bg: 'rgba(246,224,94,0.2)', color: '#F6E05E' };
      case 'contributor': return { bg: 'rgba(183,148,244,0.2)', color: '#B794F4' };
      case 'supporter': return { bg: 'rgba(66,153,225,0.2)', color: '#63B3ED' };
      default: return { bg: 'rgba(160,160,160,0.2)', color: '#aaa' };
    }
  }, [tier]);

  if (!member) return null;

  return (
    <Box
      bg="gray.800"
      borderRadius="16px"
      overflow="hidden"
      border="1px solid"
      borderColor="rgba(246,224,94,0.1)"
      boxShadow="0 8px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(246,224,94,0.05)"
      maxW="380px"
      w="100%"
      fontFamily="'Inter', -apple-system, sans-serif"
    >
      {/* Gold header strip */}
      <Box
        h="6px"
        bgGradient="linear(to-r, #F6E05E, #ED8936, #F6E05E)"
      />

      {/* Content area */}
      <Box p="20px">

        {/* ─── Profile Header ─── */}
        <Flex gap="16px" align="flex-start" mb="16px">
          {/* Avatar */}
          <Box position="relative" flexShrink={0} overflow="visible">
            <BurrowAvatar
              size="lg"
              name={member.burrowerId}
              src={member.userAvatar}
              border="none"
              boxShadow={['supporter','contributor','vip'].includes(tier) ? '0 0 12px rgba(246,224,94,0.5), 0 0 24px rgba(246,224,94,0.2)' : 'none'}
              w="72px"
              h="72px"
            />
            <ProfileOrbitStars count={localRespectCount} />
            {member.isOnline && (
              <Box
                position="absolute"
                bottom="2px"
                right="2px"
                w="14px"
                h="14px"
                bg="#48BB78"
                borderRadius="full"
                border="3px solid #1A202C"
              />
            )}
          </Box>

          {/* Header info */}
          <Box flex={1}>
            <Text fontSize="20px" fontWeight={800} color="white" mb="4px" lineHeight={1.2}>
              {member.burrowerId}
            </Text>

            {/* Online status */}
            {member.isOnline && (
              <HStack spacing="6px" mb="6px">
                <Box
                  w="8px"
                  h="8px"
                  bg="#48BB78"
                  borderRadius="full"
                  animation={`${pulseGlow} 2s infinite`}
                />
                <Text fontSize="11px" color="#48BB78">Online now</Text>
              </HStack>
            )}

            {/* Join date */}
            {joinDate && (
              <Text fontSize="11px" color="#888" mt="2px">
                Joined {joinDate}
              </Text>
            )}
          </Box>

          {/* Close button */}
          {onClose && (
            <IconButton
              icon={<CloseIcon />}
              size="xs"
              variant="ghost"
              color="gray.500"
              _hover={{ color: 'white' }}
              onClick={onClose}
              aria-label="Close profile card"
              minW="auto"
              h="auto"
              p="4px"
            />
          )}
        </Flex>


        {/* ─── Level Rank Panel ─── */}
        <RankPanel
          label="Burrow Level"
          value={level}
          valueLabel="BP"
          unknown={levelUnknown}
          ranks={LEVEL_RANKS}
          colorStart="#F6E05E"
          colorEnd="#ED8936"
          stripGradient="linear(to-r, #F6E05E, #ED8936)"
        />

        {/* ─── Respect Rank Panel ─── */}
        <RankPanel
          label="Respect"
          value={localRespectCount}
          valueLabel="Respect"
          ranks={RESPECT_RANKS}
          colorStart="#48BB78"
          colorEnd="#38B2AC"
          stripGradient="linear(to-r, #48BB78, #38B2AC)"
        />


        {/* ─── Detail Chips (Location + Membership) ─── */}
        <Flex gap="10px" mb="12px">
          <Box
            flex={1}
            bg="rgba(255,255,255,0.03)"
            borderRadius="10px"
            p="10px 12px"
            border="1px solid rgba(255,255,255,0.04)"
            textAlign="center"
          >
            <Text fontSize="9px" color="#555" textTransform="uppercase" letterSpacing="0.5px" mb="4px">
              Location
            </Text>
            <Text fontSize="14px" fontWeight={700} color={member.userState ? '#48BB78' : '#666'} fontStyle={member.userState ? 'normal' : 'italic'}>
              {member.userState || 'Anonymous'}
            </Text>
          </Box>
          <Box
            flex={1}
            bg="rgba(255,255,255,0.03)"
            borderRadius="10px"
            p="10px 12px"
            border="1px solid rgba(255,255,255,0.04)"
            textAlign="center"
          >
            <Text fontSize="9px" color="#555" textTransform="uppercase" letterSpacing="0.5px" mb="4px">
              Membership
            </Text>
            <Text fontSize="14px" fontWeight={700} color="#B794F4" textTransform="capitalize">
              {tier}
            </Text>
          </Box>
        </Flex>


        {/* ─── Bio (collapsible if long) ─── */}
        <CollapsibleBio bio={member.userBio} />


        {/* ─── Primary Actions ─── */}
        <Flex gap="8px" mb="8px">
          {!isKin && (
            <Button
              flex={1}
              size="sm"
              h="38px"
              fontSize="13px"
              fontWeight={600}
              bgGradient="linear(to-r, #F6E05E, #ED8936)"
              color="#1a1a1a"
              _hover={{ transform: 'translateY(-2px)', shadow: '0 4px 15px rgba(0,0,0,0.3)' }}
              transition="all 0.2s"
              borderRadius="8px"
              leftIcon={<FaPlus size={12} />}
              onClick={() => onAddToKin(member.burrowerId)}
            >
              Add to Kin
            </Button>
          )}
          <Button
            flex={1}
            size="sm"
            h="38px"
            fontSize="13px"
            fontWeight={600}
            bg="rgba(66,153,225,0.3)"
            border="1px solid rgba(66,153,225,0.5)"
            color="white"
            _hover={{ transform: 'translateY(-2px)', shadow: '0 4px 15px rgba(0,0,0,0.3)', bg: 'rgba(66,153,225,0.4)' }}
            transition="all 0.2s"
            borderRadius="8px"
            leftIcon={<ChatIcon boxSize={3} />}
            onClick={() => onMessage(member.burrowerId)}
          >
            Message
          </Button>
          <Button
            flex={1}
            size="sm"
            h="38px"
            fontSize="13px"
            fontWeight={600}
            bg="rgba(159,122,234,0.3)"
            border="1px solid rgba(159,122,234,0.5)"
            color="white"
            _hover={{ transform: 'translateY(-2px)', shadow: '0 4px 15px rgba(0,0,0,0.3)', bg: 'rgba(159,122,234,0.4)' }}
            transition="all 0.2s"
            borderRadius="8px"
            leftIcon={<FaTv size={12} />}
            onClick={() => {
              if (onClose) onClose();
              window.location.href = `/channel/${member.burrowerId}`;
            }}
          >
            Channel
          </Button>
        </Flex>

        {/* ─── Secondary Actions ─── */}
        <Flex gap="8px">
          <Button
            ref={respectBtnRef}
            flex={1}
            size="xs"
            h="30px"
            fontSize="11px"
            fontWeight={600}
            bg={hasGivenRespect ? 'rgba(72,187,120,0.5)' : 'rgba(72,187,120,0.3)'}
            border={`1px solid ${hasGivenRespect ? 'rgba(72,187,120,0.8)' : 'rgba(72,187,120,0.5)'}`}
            color="white"
            _hover={{ transform: 'translateY(-2px)', shadow: '0 4px 15px rgba(0,0,0,0.3)', bg: hasGivenRespect ? 'rgba(245,101,101,0.35)' : 'rgba(72,187,120,0.4)' }}
            transition="all 0.2s"
            borderRadius="8px"
            leftIcon={hasGivenRespect ? <FaCheck size={11} /> : <FaHandshake size={11} />}
            onClick={handleToggleRespect}
            isLoading={respectLoading}
          >
            {hasGivenRespect ? 'Respected' : 'Respect'}
          </Button>
          <Button
            flex={1}
            size="xs"
            h="30px"
            fontSize="11px"
            fontWeight={600}
            bg="rgba(237,137,54,0.3)"
            border="1px solid rgba(237,137,54,0.5)"
            color="white"
            _hover={{ transform: 'translateY(-2px)', shadow: '0 4px 15px rgba(0,0,0,0.3)', bg: 'rgba(237,137,54,0.4)' }}
            transition="all 0.2s"
            borderRadius="8px"
            leftIcon={<FaCoins size={11} />}
            onClick={() => onGiftMU && onGiftMU(member.burrowerId)}
          >
            Gift MU
          </Button>
          <Button
            flex={1}
            size="xs"
            h="30px"
            fontSize="11px"
            fontWeight={600}
            bg="rgba(245,101,101,0.15)"
            border="1px solid rgba(245,101,101,0.3)"
            color="#F56565"
            _hover={{ transform: 'translateY(-2px)', shadow: '0 4px 15px rgba(0,0,0,0.3)', bg: 'rgba(245,101,101,0.25)' }}
            transition="all 0.2s"
            borderRadius="8px"
            leftIcon={<FaExclamationTriangle size={10} />}
            onClick={() => onReport && onReport(member.burrowerId)}
          >
            Report
          </Button>
        </Flex>

      </Box>
    </Box>
  );
};

export default MemberProfileCard;
